import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  PreviewSnippet,
  Icons,
  Button,
  DrawerProps,
  Drawer,
  useDrawerInstance,
  notify,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import {
  Webhook,
  WebhookStatus,
  UpdateWebhook,
  SlackStatus,
  UpdateSlackConfiguration,
  DeliveryMethod,
} from '@cast/types';

import { DestroyDrawerFormGuard } from 'components/drawer/ds';
import {
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useUpdateSlackConfiguration,
  useDeleteSlackConfiguration,
} from 'hooks/mutations/notifications';
import {
  useGetSlackConfigurations,
  useGetSlackWorkspaces,
} from 'hooks/queries/notifications';

import { AlertForm } from './AlertForm';
import { AlertFormSubdrawerActions } from './AlertFormSubdrawerActions';
import { DeleteOverlay } from './DeleteOverlay';
import { FormModel } from './types';
import { makeApiModel } from './utils';
import { WebhookStatusChip } from '../../_components';

import { FormMode } from '@cast/shared-rff-ui';

type Props = DrawerProps & {
  webhook?: Webhook;
  slackConfigurationId?: string;
  restoredFormState?: FormModel;
};

export const AlertFormSubdrawer = forwardRef<HTMLDivElement, Props>(
  ({ webhook, slackConfigurationId, ...drawerProps }, ref) => {
    const { data: slackConfigurations } = useGetSlackConfigurations(
      !!slackConfigurationId
    );
    const slackConfiguration = slackConfigurations?.items?.find(
      (c) => c.id === slackConfigurationId
    );
    const alertConfig = webhook || slackConfiguration;
    const { data: slackWorkspacesData } = useGetSlackWorkspaces();

    const restoredFormState = useMemo(() => {
      // Note: URL state param cleanup now handled by Notifications.tsx
      // This component only uses restoredFormState passed via props
      return drawerProps.restoredFormState;
    }, [drawerProps.restoredFormState]);

    const { close, shouldRenderGuard, setOnCloseGuard } = useDrawerInstance();
    const [formIsDirty, setFormIsDirty] = useState(false);
    const [formMode, setFormMode] = useState<FormMode>('view');

    useEffect(() => {
      if (formMode === 'edit' && formIsDirty) {
        setOnCloseGuard(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formMode, formIsDirty]);

    const { mutateAsync: updateWebhook } = useUpdateWebhookMutation(() => {
      close();
    });

    const { mutateAsync: updateSlackConfiguration } =
      useUpdateSlackConfiguration(() => {
        close();
      });

    const [shouldRenderDeleteGuard, setOnDeleteGuard] = useState(false);
    const { mutate: deleteWebhook } = useDeleteWebhookMutation(
      () => {
        close();
      },
      () => {
        setOnDeleteGuard(false);
      }
    );

    const { mutate: deleteSlackConfiguration } = useDeleteSlackConfiguration(
      () => {
        close();
      },
      () => {
        setOnDeleteGuard(false);
      }
    );

    const handleDelete = useCallback(() => {
      if (alertConfig?.id) {
        if (slackConfiguration) {
          deleteSlackConfiguration(alertConfig.id);
        } else {
          deleteWebhook(alertConfig.id);
        }
      }
    }, [
      deleteWebhook,
      deleteSlackConfiguration,
      alertConfig?.id,
      slackConfiguration,
    ]);

    const handleSubmit = useCallback(
      async (values: FormModel) => {
        if (alertConfig?.id) {
          if (values.deliveryMethod === DeliveryMethod.Slack) {
            // Get the workspace ID from the connected workspace
            const workspaceId = slackWorkspacesData?.items?.[0]?.id;
            if (!workspaceId) {
              notify.error('Please connect a Slack workspace first');
              throw new Error('Please connect a Slack workspace first');
            }

            // Add workspaceId to the model
            const modelWithWorkspace = {
              ...values,
              slackWorkspaceId: workspaceId,
            };

            await updateSlackConfiguration({
              id: alertConfig.id,
              config: makeApiModel(
                modelWithWorkspace
              ) as UpdateSlackConfiguration,
            });
          } else if (webhook) {
            await updateWebhook({
              id: webhook.id!,
              webhook: makeApiModel(values) as UpdateWebhook,
            });
          }
        }
      },
      [
        updateWebhook,
        updateSlackConfiguration,
        alertConfig?.id,
        webhook,
        slackWorkspacesData,
      ]
    );

    const actionsContainer = useRef<HTMLDivElement | null>(null);

    return (
      <Drawer
        {...drawerProps}
        size="medium"
        ref={ref}
        hideBackdrop
        testId="alert-form-subdrawer"
      >
        <Box
          display="flex"
          flexDirection="column"
          px="32px"
          height="100%"
          overflow="auto"
        >
          <Stack position="relative" pt="58px">
            <Stack
              direction="row"
              position="absolute"
              top={10}
              right={-15}
              gap={8}
              ref={actionsContainer}
            >
              {!!actionsContainer.current && (
                <AlertFormSubdrawerActions
                  formMode={formMode}
                  onEdit={() => setFormMode('edit')}
                  onDelete={() => setOnDeleteGuard(true)}
                  container={actionsContainer.current}
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                sx={{
                  width: 28,
                  padding: 0,
                }}
                onClick={() => {
                  close();
                }}
              >
                <Icons.X size={16} />
              </Button>
            </Stack>
            {alertConfig && (
              <>
                <Typography
                  variant="h4"
                  color={tokens.theme.fg.structural.primary}
                >
                  {alertConfig.name}
                </Typography>
                <WebhookStatusChip
                  status={alertConfig.status}
                  sx={{ mt: '16px' }}
                />
                {(alertConfig.status === WebhookStatus.FAILED ||
                  alertConfig.status === SlackStatus.NOT_OK) &&
                  !!alertConfig.error && (
                    <PreviewSnippet
                      code={alertConfig.error}
                      onCopy={() => notify.success('Copied to clipboard!')}
                      sx={{
                        wordBreak: 'break-word',
                      }}
                      mt="24px"
                      p="12px 0 12px 12px"
                      maxHeight={300}
                      testId="alert-error-snippet"
                    />
                  )}
              </>
            )}
          </Stack>
          {alertConfig && (
            <AlertForm
              mode={formMode}
              webhook={webhook}
              slackConfiguration={slackConfiguration}
              restoredFormState={restoredFormState}
              onSubmit={handleSubmit}
              onCancel={() => {
                close();
              }}
              onDirty={setFormIsDirty}
            />
          )}
        </Box>
        {shouldRenderDeleteGuard && (
          <DeleteOverlay
            onDelete={handleDelete}
            onCancel={() => setOnDeleteGuard(false)}
          />
        )}
        {shouldRenderGuard && (
          <DestroyDrawerFormGuard
            onContinue={() => {
              setOnCloseGuard(false);
              setTimeout(() => {
                if (formIsDirty) {
                  setOnCloseGuard(true);
                }
              }, 0);
            }}
            onLeave={() => close()}
          />
        )}
      </Drawer>
    );
  }
);

AlertFormSubdrawer.displayName = 'AlertFormSubdrawer';
